import React, { useState, useEffect } from "react"
import { Box, Button, Chip, Typography } from "@mui/material"
import { useTheme } from "@mui/material/styles"
import type { ChipProps, Theme } from "@mui/material"
import { useTasks } from "@crm-src/hooks/useTasks"
import type { Task } from "@crm-src/types/task"
import { TaskCalendarSkeleton } from "@crm-src/components/task-calendar-skeleton"
import {
  ChevronLeft,
  ChevronRight,
  Clock,
  User,
  Repeat
} from "lucide-react"
import { format, addDays, startOfWeek, endOfWeek, isSameDay, parseISO, addWeeks, subWeeks } from "date-fns"

interface TaskCalendarProps {
  className?: string
  onTaskClick?: (task: Task) => void
  onDateClick?: (date: Date) => void
  filteredTasks?: Task[]
  filterStatus?: string
  filterPriority?: string
  searchTerm?: string
}

// Task priority → MUI Chip palette color (inlined during the MUI 5 migration
// from the former Tailwind badge classes).
const priorityChipColor: Record<NonNullable<Task["priority"]>, ChipProps["color"]> = {
  low: "default",
  medium: "info",
  high: "warning",
  urgent: "error",
}

// Task status → left-border accent color + completed/cancelled dimming.
// Resolved against the live theme so it tracks light/dark mode.
function statusAccent(status: Task["status"], theme: Theme): { borderColor: string; opacity: number } {
  switch (status) {
    case "pending":
      return { borderColor: theme.palette.warning.main, opacity: 1 }
    case "in_progress":
      return { borderColor: theme.palette.info.main, opacity: 1 }
    case "completed":
      return { borderColor: theme.palette.success.main, opacity: 0.6 }
    case "cancelled":
      return { borderColor: theme.palette.text.disabled, opacity: 0.6 }
    default:
      return { borderColor: theme.palette.divider, opacity: 1 }
  }
}

export function TaskCalendar({ className, onTaskClick, onDateClick, filteredTasks, filterStatus, filterPriority, searchTerm }: TaskCalendarProps) {
  const theme = useTheme()
  const [currentDate, setCurrentDate] = useState(new Date())
  const [view, setView] = useState<'week' | 'month'>('week')
  const [calendarTasks, setCalendarTasks] = useState<Task[]>([])
  const [isFetchingTasks, setIsFetchingTasks] = useState(false)

  const { isLoading, getCalendarTasks } = useTasks()

  // Store the fetch function in a ref to avoid recreating it
  const fetchTasksRef = React.useRef<(() => Promise<void>) | null>(null)

  // Load calendar data from API when date range changes
  useEffect(() => {
    const loadCalendarData = async () => {
      const startDate = view === 'week'
        ? startOfWeek(currentDate)
        : new Date(currentDate.getFullYear(), currentDate.getMonth(), 1)

      const endDate = view === 'week'
        ? endOfWeek(currentDate)
        : new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0)

      try {
        setIsFetchingTasks(true)

        // Build params with filters
        const params: any = {
          start_date: format(startDate, 'yyyy-MM-dd'),
          end_date: format(endDate, 'yyyy-MM-dd')
        }

        // Add status filter if not "all"
        if (filterStatus && filterStatus !== 'all') {
          params.status = filterStatus
        }

        // Add priority filter if not "all"
        if (filterPriority && filterPriority !== 'all') {
          params.priority = filterPriority
        }

        // Fetch tasks from API for the visible date range with filters
        const response = await getCalendarTasks(params)

        // Extract tasks array from response
        let fetchedTasks = response?.data || []

        // Apply client-side search filter (API doesn't support search yet)
        if (searchTerm && searchTerm.trim() !== '') {
          const lowerSearch = searchTerm.toLowerCase()
          fetchedTasks = fetchedTasks.filter((task: Task) =>
            task.name.toLowerCase().includes(lowerSearch) ||
            (task.description?.toLowerCase() || '').includes(lowerSearch)
          )
        }

        // Use the filtered tasks
        setCalendarTasks(fetchedTasks)
      } catch (error) {
        console.error('Failed to fetch calendar tasks:', error)
        setCalendarTasks([])
      } finally {
        setIsFetchingTasks(false)
      }
    }

    // Store the function in ref for event handlers
    fetchTasksRef.current = loadCalendarData

    // Load immediately
    loadCalendarData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDate, view, filterStatus, filterPriority, searchTerm])

  // Listen for task changes (create, update, delete, complete)
  useEffect(() => {
    const handleTasksRefresh = () => {
      // Use the ref to avoid dependency issues
      if (fetchTasksRef.current) {
        fetchTasksRef.current()
      }
    }

    // Listen for various task events
    window.addEventListener('refreshTasks', handleTasksRefresh)
    window.addEventListener('taskCreated', handleTasksRefresh)
    window.addEventListener('taskUpdated', handleTasksRefresh)
    window.addEventListener('taskDeleted', handleTasksRefresh)
    window.addEventListener('taskCompleted', handleTasksRefresh)

    return () => {
      window.removeEventListener('refreshTasks', handleTasksRefresh)
      window.removeEventListener('taskCreated', handleTasksRefresh)
      window.removeEventListener('taskUpdated', handleTasksRefresh)
      window.removeEventListener('taskDeleted', handleTasksRefresh)
      window.removeEventListener('taskCompleted', handleTasksRefresh)
    }
  }, [])

  const navigateDate = (direction: 'prev' | 'next') => {
    if (view === 'week') {
      setCurrentDate(direction === 'next' ? addWeeks(currentDate, 1) : subWeeks(currentDate, 1))
    } else {
      const newDate = new Date(currentDate)
      newDate.setMonth(newDate.getMonth() + (direction === 'next' ? 1 : -1))
      setCurrentDate(newDate)
    }
  }

  const getTasksForDate = (date: Date) => {
    return calendarTasks.filter(task =>
      task.due_date && isSameDay(parseISO(task.due_date), date)
    )
  }

  const renderWeekView = () => {
    const startDate = startOfWeek(currentDate)
    const days = Array.from({ length: 7 }, (_, i) => addDays(startDate, i))

    return (
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 0.5 }}>
        {/* Header */}
        {days.map((day, index) => {
          const dayTasks = getTasksForDate(day)
          const taskCount = dayTasks.length

          return (
            <Box key={index} sx={{ p: 1, borderBottom: 1, borderColor: "divider", fontWeight: 500, textAlign: "center", bgcolor: "background.paper", position: "sticky", top: 0, zIndex: 10 }}>
              <Typography sx={{ fontSize: "0.875rem", color: "text.secondary" }}>{format(day, 'EEE')}</Typography>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                <Box
                  sx={{
                    fontSize: "1.125rem",
                    ...(isSameDay(day, new Date())
                      ? { bgcolor: "info.main", color: "info.contrastText", borderRadius: "50%", width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center" }
                      : {}),
                  }}
                >
                  {format(day, 'd')}
                </Box>
                {taskCount > 0 && (
                  <Box sx={{ bgcolor: (t) => `${t.palette.info.main}1f`, color: "info.main", fontSize: "0.75rem", fontWeight: 600, borderRadius: 999, px: 1, py: 0.25 }}>
                    {taskCount}
                  </Box>
                )}
              </Box>
            </Box>
          )
        })}

        {/* Task columns with scrollable containers */}
        {days.map((day, index) => {
          const dayTasks = getTasksForDate(day)

          return (
            <Box
              key={index}
              sx={{ position: "relative", borderRight: 1, borderColor: "divider", "&:hover .scroll-fade": { opacity: 1 } }}
            >
              {/* Scrollable task container */}
              <Box
                sx={{
                  height: 500,
                  overflowY: "auto",
                  overflowX: "hidden",
                  p: 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  scrollBehavior: "smooth",
                  cursor: "pointer",
                  transition: "background-color 0.15s",
                  "&:hover": { bgcolor: (t) => `${t.palette.action.hover}80` },
                }}
                onClick={(e) => {
                  // Only trigger if clicking on the background, not on tasks
                  if (e.target === e.currentTarget) {
                    onDateClick?.(day)
                  }
                }}
              >
                {dayTasks.length === 0 ? (
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "text.disabled", fontSize: "0.875rem" }}>
                    No tasks
                  </Box>
                ) : (
                  dayTasks.map((task) => {
                    const accent = statusAccent(task.status, theme)
                    return (
                      <Box
                        key={task.id}
                        sx={{
                          fontSize: "0.75rem",
                          p: 1.25,
                          borderRadius: 1,
                          borderLeft: "4px solid",
                          borderLeftColor: accent.borderColor,
                          opacity: accent.opacity,
                          bgcolor: "background.paper",
                          boxShadow: 1,
                          cursor: "pointer",
                          transition: "all 0.15s",
                          "&:hover": { boxShadow: 3, transform: "scale(1.02) translateX(2px)" },
                        }}
                        onClick={(e) => {
                          e.stopPropagation()
                          onTaskClick?.(task)
                        }}
                      >
                        {/* Row 1: Task name */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                          <Box sx={{ fontWeight: 500, flex: 1, color: "text.primary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{task.name}</Box>
                          {(task.is_recurring || task.is_recurring_instance) && (
                            <Box component="span" sx={{ display: "flex", color: "info.main", flexShrink: 0 }}><Repeat size={12} /></Box>
                          )}
                        </Box>

                        {/* Row 2: Time and Priority on same line */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                          <Box sx={{ color: "text.secondary", fontSize: "0.75rem", display: "flex", alignItems: "center", gap: 0.5 }}>
                            <Clock size={12} />
                            {format(parseISO(task.due_date), 'HH:mm')}
                          </Box>
                          {task.priority && (
                            <Chip
                              size="small"
                              label={task.priority}
                              color={priorityChipColor[task.priority] ?? "default"}
                              sx={{ height: 18, fontSize: "10px", "& .MuiChip-label": { px: 0.75 } }}
                            />
                          )}
                        </Box>

                        {/* Row 3: Sales person */}
                        {task.assigned_to && (
                          <Box sx={{ color: "text.secondary", display: "flex", alignItems: "center", gap: 0.5 }}>
                            <User size={12} />
                            <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: "0.75rem" }}>{task.assigned_to.full_name}</Box>
                          </Box>
                        )}
                      </Box>
                    )
                  })
                )}
              </Box>

              {/* Scroll indicator shadow at bottom */}
              {dayTasks.length > 3 && (
                <Box
                  className="scroll-fade"
                  sx={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: 32,
                    background: (t) => `linear-gradient(to top, ${t.palette.action.hover}cc, transparent)`,
                    pointerEvents: "none",
                    opacity: 0,
                    transition: "opacity 0.15s",
                  }}
                />
              )}
            </Box>
          )
        })}
      </Box>
    )
  }

  const renderMonthView = () => {
    const startDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1)
    const endDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0)
    const startCalendar = startOfWeek(startDate)
    const endCalendar = endOfWeek(endDate)

    const days = []
    let current = startCalendar
    while (current <= endCalendar) {
      days.push(new Date(current))
      current = addDays(current, 1)
    }

    return (
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 0.5 }}>
        {/* Header */}
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
          <Box key={day} sx={{ p: 1, fontWeight: 500, textAlign: "center", color: "text.secondary" }}>
            {day}
          </Box>
        ))}

        {/* Calendar grid */}
        {days.map((day, index) => {
          const isCurrentMonth = day.getMonth() === currentDate.getMonth()
          const isToday = isSameDay(day, new Date())
          const dayTasks = getTasksForDate(day)

          return (
            <Box
              key={index}
              sx={{
                p: 1,
                minHeight: 100,
                border: 1,
                borderColor: "divider",
                cursor: "pointer",
                transition: "background-color 0.15s",
                "&:hover": { bgcolor: (t) => `${t.palette.info.main}14` },
                ...(!isCurrentMonth ? { color: "text.disabled", bgcolor: "action.hover" } : {}),
                ...(isToday ? { bgcolor: (t) => `${t.palette.info.main}14`, borderColor: "info.main" } : {}),
              }}
              onClick={() => onDateClick?.(day)}
            >
              <Box
                sx={{
                  fontSize: "0.875rem",
                  mb: 0.5,
                  ...(isToday ? { fontWeight: 700, color: "info.main" } : {}),
                }}
              >
                {format(day, 'd')}
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                {dayTasks.slice(0, 2).map((task) => {
                  const accent = statusAccent(task.status, theme)
                  return (
                    <Box
                      key={task.id}
                      sx={{
                        fontSize: "0.75rem",
                        p: 0.5,
                        borderRadius: 1,
                        borderLeft: "2px solid",
                        borderLeftColor: accent.borderColor,
                        opacity: accent.opacity,
                        bgcolor: "background.paper",
                        cursor: "pointer",
                        "&:hover": { bgcolor: "action.hover" },
                      }}
                      onClick={(e) => {
                        e.stopPropagation()
                        onTaskClick?.(task)
                      }}
                    >
                      <Box sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: 500 }}>{task.name}</Box>
                      <Box sx={{ color: "text.secondary", fontSize: "0.75rem", display: "flex", alignItems: "center" }}>
                        <Box component="span" sx={{ display: "flex", mr: 0.5 }}><Clock size={12} /></Box>
                        {format(parseISO(task.due_date), 'HH:mm')}
                      </Box>
                    </Box>
                  )
                })}
                {dayTasks.length > 2 && (
                  <Box sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
                    +{dayTasks.length - 2} more
                  </Box>
                )}
              </Box>
            </Box>
          )
        })}
      </Box>
    )
  }

  return (
    <Box {...(className ? { className } : {})}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, color: "text.secondary" }}>
            {format(currentDate, 'MMMM yyyy')} • {view === 'week' ? 'Week' : 'Month'} View
          </Typography>
          {isFetchingTasks && (
            <Typography sx={{ fontSize: "0.75rem", color: "text.secondary", mt: 0.5 }}>Loading tasks...</Typography>
          )}
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {/* View toggle */}
          <Box sx={{ display: "flex", bgcolor: "action.hover", borderRadius: 1, p: 0.5 }}>
            <Button
              variant={view === 'week' ? 'contained' : 'text'}
              color={view === 'week' ? 'primary' : 'inherit'}
              size="small"
              onClick={() => setView('week')}
              sx={{ fontSize: "0.75rem" }}
              disabled={isFetchingTasks}
            >
              Week
            </Button>
            <Button
              variant={view === 'month' ? 'contained' : 'text'}
              color={view === 'month' ? 'primary' : 'inherit'}
              size="small"
              onClick={() => setView('month')}
              sx={{ fontSize: "0.75rem" }}
              disabled={isFetchingTasks}
            >
              Month
            </Button>
          </Box>

          {/* Navigation */}
          <Button
            variant="outlined"
            color="inherit"
            size="small"
            onClick={() => navigateDate('prev')}
            disabled={isFetchingTasks}
          >
            <ChevronLeft size={16} />
          </Button>
          <Button
            variant="outlined"
            color="inherit"
            size="small"
            onClick={() => setCurrentDate(new Date())}
            disabled={isFetchingTasks}
          >
            Today
          </Button>
          <Button
            variant="outlined"
            color="inherit"
            size="small"
            onClick={() => navigateDate('next')}
            disabled={isFetchingTasks}
          >
            <ChevronRight size={16} />
          </Button>
        </Box>
      </Box>


      {isFetchingTasks && calendarTasks.length === 0 ? (
        <TaskCalendarSkeleton view={view} />
      ) : (
        <>
          {view === 'week' ? renderWeekView() : renderMonthView()}
        </>
      )}
    </Box>
  )
}
